import React, {useCallback, useEffect, useRef, useState} from 'react'
import {ArrowChevronLeft} from '@UI/components/icons/ArrowChevronLeft.tsx'
import {ArrowChevronRight} from '@UI/components/icons/ArrowChevronRight.tsx'
import styles from './scrollableFilterToggle.module.less'

interface ScrollableFilterToggleProps {
    children: React.ReactNode
}

export const ScrollableFilterToggle: React.FC<ScrollableFilterToggleProps> = ({children}) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    const checkScroll = useCallback(() => {
        const el = scrollRef.current
        if (!el) return
        setCanScrollLeft(el.scrollLeft > 1)
        setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1)
    }, [])

    useEffect(() => {
        const el = scrollRef.current
        if (!el) return
        checkScroll()
        el.addEventListener('scroll', checkScroll, {passive: true})
        const observer = new ResizeObserver(checkScroll)
        observer.observe(el)
        return () => {
            el.removeEventListener('scroll', checkScroll)
            observer.disconnect()
        }
    }, [checkScroll])

    const scroll = useCallback((direction: 'left' | 'right') => {
        const el = scrollRef.current
        if (!el) return
        el.scrollBy({left: direction === 'left' ? -120 : 120, behavior: 'smooth'})
    }, [])

    return (
        <div className={styles.wrapper}>
            {canScrollLeft && (
                <button className={`${styles.arrow} ${styles.arrowLeft}`} onClick={() => scroll('left')}>
                    <ArrowChevronLeft size={14}/>
                </button>
            )}
            <div ref={scrollRef} className={styles.scrollArea}>
                {children}
            </div>
            {canScrollRight && (
                <button className={`${styles.arrow} ${styles.arrowRight}`} onClick={() => scroll('right')}>
                    <ArrowChevronRight size={14}/>
                </button>
            )}
        </div>
    )
}
